import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export interface StructuredDropdownOption {
  value: string;
  columns: string[];
}

export type DropdownOption = string | StructuredDropdownOption;

interface OptionDropDownProps {
  value: string;
  onChange: (value: string) => void;
  isOpen: boolean;
  options: DropdownOption[];
  triggerRef?: React.RefObject<HTMLDivElement>;
}

function getOptionValue(option: DropdownOption): string {
  return typeof option === "string" ? option : option.value;
}

function getOptionSearchText(option: DropdownOption): string {
  return typeof option === "string" ? option.replace(/\u00A0/g, " ") : option.columns.join(" ").replace(/\u00A0/g, " ");
}

const OptionDropDown: React.FC<OptionDropDownProps> = ({ value, onChange, isOpen, options, triggerRef }) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });

  useEffect(() => {
    if (isOpen && triggerRef?.current) {
      const updatePosition = () => {
        const rect = triggerRef.current?.getBoundingClientRect();
        if (rect) {
          setPosition({
            top: rect.bottom + window.scrollY,
            left: rect.left + window.scrollX,
            width: rect.width
          });
        }
      };

      updatePosition();
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);

      return () => {
        window.removeEventListener("scroll", updatePosition, true);
        window.removeEventListener("resize", updatePosition);
      };
    }
  }, [isOpen, triggerRef]);

  if (!isOpen || options.length === 0) return null;

  const filteredOptions = options.filter((option) =>
    getOptionSearchText(option).toLowerCase().includes(value.toLowerCase())
  );

  if (filteredOptions.length === 0) return null;

  const isColumnar = filteredOptions.some((o) => typeof o !== "string");

  const dropdownContent = (
    <div
      ref={dropdownRef}
      className="fixed z-[9999] mt-1"
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
        width: `${position.width}px`
      }}
    >
      <div className="bg-white border border-secondary-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
        {isColumnar ? (
          <table className="w-full border-collapse">
            <tbody>
              {filteredOptions.map((option, index) => {
                const cols = typeof option === "string" ? [option] : option.columns;
                return (
                  <tr
                    key={`${getOptionValue(option)}-${index}`}
                    className="hover:bg-primary-50 transition-colors duration-150 border-b border-secondary-100 last:border-0 cursor-pointer"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      onChange(getOptionValue(option));
                    }}
                  >
                    {cols.map((col, colIndex) => (
                      <td
                        key={colIndex}
                        className={`px-4 py-2 text-sm whitespace-nowrap text-secondary-700 ${colIndex === 0 ? "font-medium" : ""}`}
                      >
                        {col}
                      </td>
                    ))}
                    <td className="w-full" />
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          filteredOptions.map((option, index) => (
            <button
              key={`${getOptionValue(option)}-${index}`}
              type="button"
              className="w-full px-4 py-2 text-left text-sm text-secondary-700 hover:bg-primary-50 hover:text-primary-700 transition-colors duration-150 first:rounded-t-lg last:rounded-b-lg focus:outline-none focus:bg-primary-100"
              onMouseDown={(e) => {
                e.preventDefault();
                onChange(getOptionValue(option));
              }}
            >
              {getOptionValue(option)}
            </button>
          ))
        )}
      </div>
    </div>
  );

  return createPortal(dropdownContent, document.body);
};

export { OptionDropDown };
